<template>
  <div class="hub-directory container" data-test-hub-directory>
    <AdnBreadcrumbs :breadcrumbs="breadcrumbs" class="breadcrumbs" />

    <div v-if="hubDirectory" class="hub-directory__content">
      <div>
        <div>
          <h1>{{ hubDirectory.title }}</h1>
          <p>{{ hubDirectory.description }}</p>
        </div>

        <div v-if="hubDirectory.hubs && hubDirectory.hubs.length > 0" class="hub-items">
          <idf-directory-card
            v-for="(hub, index) in hubDirectory.hubs"
            :key="hub.id"
            :index="index"
            :description="hub.description"
            :image-url="hub.imageUrl"
            :image-alt="hub.imageAlt"
            :style="`order: ${index + 1};`"
            :title="hub.title"
            formatted-date=""
            number-files-label=""
            @click="onHubClick(hub)"
          />
          <PNewsletterSubscription v-if="isAladin" form-target-id="articlesItemsNL" style="order: 3" />

          <template v-if="hubDirectory.advertorialInserts">
            <p-advertorial-insert
              v-for="(advertorial, adIndex) in hubDirectory.advertorialInserts"
              :key="advertorial.id"
              :style="`order: ${adIndex === 0 ? '6' : '10'}`"
              :value="advertorial"
              @card-visible="onVisibilityChanged(advertorial)"
            />
          </template>
          <PArticleSocialButtons
            v-if="hubDirectory.title"
            :image-src="hubDirectory.imageUrl"
            :style="`order: ${hubDirectory.hubs.length + 20}`"
            :title="hubDirectory.title"
            data-test-share
            label="Partager la page"
          />
        </div>
      </div>

      <div class="hub-directory__advertorials">
        <PArticleSocialButtons
          v-if="hubDirectory.title"
          :image-src="hubDirectory.imageUrl"
          :title="hubDirectory.title"
          data-test-share
          label="Partager la page"
        />
        <PNewsletterSubscription v-if="isAladin" />
        <div v-if="hubDirectory.advertorialInserts" class="advertorialInserts">
          <p-advertorial-insert
            v-for="advertorial in hubDirectory.advertorialInserts"
            :key="advertorial.id"
            :value="advertorial"
            @card-visible="onVisibilityChanged(advertorial)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import { type Ref, ref, useHead, useAsyncData, createError } from '#imports'
import AdnBreadcrumbs from '@b2ag/design-system/src/components/AdnBreadcrumbs/AdnBreadcrumbs.vue'
import IdfDirectoryCard from '@b2ag/design-system/src/components/IdfDirectoryCard/IdfDirectoryCard.vue'
import type { BreadCrumb } from '@b2ag/search/src/types/BreadCrumb'
import { isAladin } from '@b2ag/app-brands'
import type { Hub } from '../../../domain/Hub'
import type { AdvertorialInsert } from '../../../domain/Advertorialnsert'
import PNewsletterSubscription from '../../presenters/PNewsletterSubscription.vue'
import PAdvertorialInsert from '../../presenters/PAdvertorialInsert/PAdvertorialInsert.vue'
import { mapHubMetaInfo } from '../../composable/hub.meta-info.mappers'
import PArticleSocialButtons from '../../presenters/PArticleSocialButtons.vue'
import { iHubRepository } from '../../../dependency-injection'

const route = useRoute()
const router = useRouter()

const breadcrumbs: Ref<BreadCrumb[]> = ref([{ name: 'Accueil', route: { name: 'home' } }])

let advertorialInsertsVisible: Array<string> = []
let timeoutHandler

const onHubClick = (hub: Hub): void => {
  const payload = {
    content_title: hub.title,
  }
  useAnalyticService().trackEvent(`hub_click_hub`, payload)
  router?.push({ name: 'hub-page', params: { slug: hub.id } })
}

const { data: hubDirectory } = await useAsyncData(
  'hubDirectory-' + route.params.slug,
  async () => await iHubRepository.getBook(route.params.slug as string),
)

if (!hubDirectory.value) {
  throw createError({ statusCode: 404, fatal: true })
}

breadcrumbs.value.push({ name: hubDirectory.value?.title } as BreadCrumb)

function onVisibilityChanged(advertorial: AdvertorialInsert) {
  if (timeoutHandler) {
    clearTimeout(timeoutHandler)
  }
  advertorialInsertsVisible.push(advertorial.id)
  timeoutHandler = setTimeout(() => {
    useAnalyticService().trackEvent('view_advertorial_insert', {
      content_title: advertorialInsertsVisible,
    })
    advertorialInsertsVisible = []
  }, 500)
}

useHead(() => {
  return mapHubMetaInfo(hubDirectory.value, route.fullPath)
})
</script>

<style lang="scss">
.hub-directory {
  margin-top: var(--spacing-5);
  margin-bottom: var(--spacing-6);

  .breadcrumbs {
    margin-bottom: var(--spacing-2);
  }

  &__content {
    background-color: var(--color-background-light);
    border-radius: var(--border-radius-2);
    display: flex;
    padding: var(--spacing-4);

    p {
      text-align: justify;
    }

    > :first-child {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: var(--spacing-6);
      padding: var(--spacing-3);

      .p-advertorial-insert {
        display: none;
      }

      .c-article-social-buttons {
        display: none;
      }

      .idf-newsletter-subscription__container {
        display: none;
      }

      .hub-items {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: var(--spacing-6);

        .directory-card {
          max-height: 40rem;
          width: 100%;

          @media (min-width: $breakpoint-lg) {
            width: calc(50% - #{var(--spacing-6)}/ 2);
          }

          cursor: pointer;

          &:hover {
            .directory-card__image {
              border-color: var(--color-blue);
              box-shadow: var(--shadow-dark);
            }
          }
        }
      }
    }

    > :last-child {
      flex: 0 0 28rem;

      .advertorialInserts {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-6);
        margin-top: var(--spacing-6);
      }

      .idf-newsletter-subscription__container {
        margin-top: var(--spacing-6);
      }
    }

    .idf-newsletter-subscription__description {
      text-align: left;
    }
  }
}

@media screen and (max-width: ($breakpoint-md - 1)) {
  .hub-directory {
    margin-top: 0;
    margin-bottom: 7rem;
    padding: 0;

    .breadcrumbs {
      display: none;
    }

    &__content {
      border-radius: initial;
      padding: 0;

      > :last-child {
        display: none;
      }

      > :first-child {
        @media screen and (min-width: $breakpoint-sm) {
          padding: var(--spacing-4);
        }

        .hub-items {
          .idf-newsletter-subscription__container {
            display: flex;
          }

          .p-advertorial-insert {
            display: initial;
          }

          .c-article-social-buttons {
            display: flex;
          }

          .directory-card {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
